import React, { useState, useMemo } from 'react';

interface DatePickerProps {
  value: string | null; // yyyy-MM-dd
  onChange: (value: string) => void;
  min?: string; // yyyy-MM-dd
  max?: string; // yyyy-MM-dd
  /** Which date the calendar opens to when no value is selected. Defaults to min. */
  defaultView?: 'min' | 'max';
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function parseDate(str: string): Date {
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function formatDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

const DatePicker: React.FC<DatePickerProps> = ({ value, onChange, min, max, defaultView = 'min' }) => {
  const selected = value ? parseDate(value) : null;
  const minDate = min ? parseDate(min) : null;
  const maxDate = max ? parseDate(max) : null;

  const fallbackDate = defaultView === 'max' ? (maxDate ?? minDate) : (minDate ?? maxDate);

  const [viewYear, setViewYear] = useState(() => selected?.getFullYear() ?? fallbackDate?.getFullYear() ?? new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(() => selected?.getMonth() ?? fallbackDate?.getMonth() ?? new Date().getMonth());

  const calendarDays = useMemo(() => {
    const firstOfMonth = new Date(viewYear, viewMonth, 1);
    // Monday = 0 in our grid
    let startDay = firstOfMonth.getDay() - 1;
    if (startDay < 0) startDay = 6;

    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const days: (Date | null)[] = [];

    // Fill leading empty slots
    for (let i = 0; i < startDay; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(new Date(viewYear, viewMonth, d));

    return days;
  }, [viewYear, viewMonth]);

  const goToPrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(viewYear - 1);
    } else {
      setViewMonth(viewMonth - 1);
    }
  };

  const goToNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(viewYear + 1);
    } else {
      setViewMonth(viewMonth + 1);
    }
  };

  const isDisabled = (d: Date): boolean => {
    if (minDate && d < minDate) return true;
    if (maxDate && d > maxDate) return true;
    return false;
  };

  const isSelected = (d: Date): boolean => {
    return selected !== null && isSameDay(d, selected);
  };

  const isToday = (d: Date): boolean => {
    return isSameDay(d, new Date());
  };

  const handleSelect = (d: Date) => {
    if (!isDisabled(d)) {
      onChange(formatDate(d));
    }
  };

  const navBtnStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    color: 'var(--color-text-light)',
    fontSize: 18,
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: 8,
    transition: 'background 0.15s',
    opacity: 0.7,
  };

  return (
    <div style={{
      background: 'rgba(250,253,251,0.08)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      border: '1px solid rgba(250,253,251,0.16)',
      borderRadius: 20,
      padding: '20px',
      width: '100%',
      boxSizing: 'border-box',
    }}>
      {/* Month/Year header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16,
      }}>
        <button type="button" onClick={goToPrevMonth} style={navBtnStyle}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(250,253,251,0.08)'}
          onMouseLeave={e => e.currentTarget.style.background = 'none'}>
          ‹
        </button>
        <span style={{
          fontFamily: "'Figtree', sans-serif",
          fontSize: 16,
          fontWeight: 400,
          color: 'var(--color-text-light)',
        }}>
          {MONTH_NAMES[viewMonth]} {viewYear}
        </span>
        <button type="button" onClick={goToNextMonth} style={navBtnStyle}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(250,253,251,0.08)'}
          onMouseLeave={e => e.currentTarget.style.background = 'none'}>
          ›
        </button>
      </div>

      {/* Day labels */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: 2,
        marginBottom: 4,
      }}>
        {DAY_LABELS.map((label) => (
          <div key={label} style={{
            textAlign: 'center',
            fontFamily: "'Figtree', sans-serif",
            fontSize: 12,
            color: 'rgba(250,253,251,0.4)',
            padding: '4px 0',
          }}>
            {label}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: 2,
      }}>
        {calendarDays.map((day, i) => {
          if (!day) {
            return <div key={`empty-${i}`} />;
          }

          const disabled = isDisabled(day);
          const sel = isSelected(day);
          const today = isToday(day);

          return (
            <button
              key={day.getTime()}
              type="button"
              onClick={() => handleSelect(day)}
              disabled={disabled}
              style={{
                width: '100%',
                aspectRatio: '1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: sel
                  ? 'var(--color-primary)'
                  : 'transparent',
                border: today && !sel
                  ? '1px solid rgba(250,253,251,0.24)'
                  : '1px solid transparent',
                borderRadius: 12,
                color: sel
                  ? 'var(--color-text-primary)'
                  : disabled
                    ? 'rgba(250,253,251,0.2)'
                    : 'var(--color-text-light)',
                fontFamily: "'Figtree', sans-serif",
                fontSize: 14,
                fontWeight: 400,
                cursor: disabled ? 'default' : 'pointer',
                transition: 'all 0.15s ease',
                padding: 0,
              }}
              onMouseEnter={(e) => {
                if (!disabled && !sel) {
                  e.currentTarget.style.background = 'rgba(250,253,251,0.08)';
                }
              }}
              onMouseLeave={(e) => {
                if (!disabled && !sel) {
                  e.currentTarget.style.background = 'transparent';
                }
              }}
            >
              {day.getDate()}
            </button>
          );
        })}
      </div>

      {/* Selected date display */}
      {selected && (
        <div style={{
          marginTop: 16,
          textAlign: 'center',
          fontFamily: "'Figtree', sans-serif",
          fontSize: 14,
          color: 'rgba(250,253,251,0.5)',
        }}>
          {selected.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        </div>
      )}
    </div>
  );
};

export default DatePicker;
